import { lazy } from 'react';
import type { ModuleManifest } from '../../lib/host/types';
import { inspectionsKeys } from './queries/keys';

// Capture module (P05 §9.2; W4 row 3; story 5-10b) — the phone-first runtime
// form an inspector actually fills in.
//
// ── WHY A MODULE, AND WHY ITS OWN ONE ─────────────────────────────────────
// The surface attaches through the module host, so `src/App.tsx` and
// `src/lib/registries/index.ts` are NOT edited (DOD §1.8). It is separate from
// `scheduling` because the two answer different questions and are gated
// differently: scheduling is `scheduling.core` ("plan and assign the work"),
// capture is `inspections.core` ("run the inspection") — the very key the
// backend's answer routes sit behind (`requireFeature('inspections.core')` in
// `routes/v1/inspection.route.js`). Folding capture into scheduling would gate
// the field worker's screen on the planner's feature, which is wrong in both
// directions: an org entitled to run inspections but not to schedule them would
// lose capture, and P18 §1's "delete the folder and the platform still compiles"
// test would stop meaning anything for either half.
//
// ── NO NAV ROW, AND THAT IS DELIBERATE ────────────────────────────────────
// Capture is reached with an inspection id in hand — from My day's Start/Resume
// (P05 §8: "routes Start/Resume directly to capture"), which this story points
// at `/app/inspections/:id/capture`. A nav row would have to invent an id, and
// a row that leads to "which inspection?" is worse than no row. The route still
// carries its own `permission`, so a persona who cannot read inspections cannot
// reach the page by typing the URL either (story 2-20).
//
// ── PERMISSION ────────────────────────────────────────────────────────────
// `inspections:read`, matching the backend's READ gate on the same route. NOT
// `inspections:create`: a reviewer and a manager may legitimately OPEN capture
// to look at a submitted inspection read-only (P11 §2 gates the read on the key
// every one of those principals holds and scope-filters the row). Whether the
// person may WRITE is the server's answer, carried on the payload as `readOnly`,
// and the screen renders it — never a second guess assembled from a role.
//
// ── NO `entities` / `search` BLOCK ────────────────────────────────────────
// G-21 does not apply: this module introduces no new record type. An Inspection
// is an existing entity owned elsewhere, and an answer set is not independently
// findable — you reach answers through the inspection that owns them.
export const manifest: ModuleManifest = {
  key: 'capture',
  featureKeys: ['inspections.core'],
  routes: [
    {
      path: '/app/inspections/:id/capture',
      element: lazy(() => import('./pages/CapturePage').then((m) => ({ default: m.CapturePage }))),
      chrome: 'shell',
      permission: 'inspections:read',
      // P16 §6.1 workspace family (story 2-146c): a nested AppFrame
      // (SectionNav rail → the answer form canvas → InspectionLifecycleBar
      // inspector) inside AppShell's own shell chrome — see CapturePage.tsx's
      // own composition comment for why it is nested rather than full-screen
      // like the Template/Report editor.
      layoutFamily: 'workspace',
    },
  ],
  // P09 §2.1 parity — the gateway capabilities behind this surface's endpoints
  // (`agents/src/capabilities/inspections.js`; answers from 5-10a, evidence from
  // 5-10c). Three clients of one contract, DOD §1.3/§1.10.
  //
  // `inspection_evidence_upload` is absent and that is DELIBERATE, not an
  // oversight: the gateway carries JSON tool calls, a model has no bytes, and the
  // only capability it could offer would take a description of a photo — the
  // simulated evidence P05 §9.3 forbids. `agents/test/smoke.mjs` asserts the
  // absence so re-adding it has to be a decision.
  agentTools: [
    'inspection_answers_get',
    'inspection_answers_patch',
    'inspection_evidence_list',
    'inspection_evidence_update',
    'inspection_evidence_delete',
  ],
  queryKeys: inspectionsKeys,
};
